'use client';
import { Box, Flex, Image, Text } from '@skin-seoul/design-system';
import type { ComponentProps } from 'react';
import { useUser, useUserService, useLoyaltyService, getMediaOrigin } from '@skin-seoul/service';
import { Condition } from '@skin-seoul/utils';

const PROFILE_BUTTON_TEST_ID = 'profile-button';

// Image path constants
const PROFILE_IMAGE_PATHS = {
  USER_LOGO: '/profile-logo-images/user-logo-image.png',
  GUEST_LOGO: '/profile-logo-images/guest-logo.png',
} as const;

type ProfileButtonProps = ComponentProps<typeof Flex>;

export const ProfileButton = ({ ...rest }: ProfileButtonProps) => {
  const { isAuthenticated, isLoading, user: currentUser } = useUser();
  const { useGetCookieUser } = useUserService();
  const { useGetCustomerBalance } = useLoyaltyService();

  const { data: user, isLoading: isLoadingUser } = useGetCookieUser({ enabled: isAuthenticated });
  const { data: balanceData } = useGetCustomerBalance({
    customerId: String(currentUser?.id ?? ''),
    enabled: isAuthenticated && Boolean(currentUser?.id),
  });

  // Get user's first name, limit to 15 characters as per Figma annotation
  const displayName = user?.display_name || 'Guest';

  const points = balanceData?.availablePoints ?? 0;

  if (isLoading || isLoadingUser) {
    return null;
  }

  return (
    <Flex
      alignItems="center"
      cursor="pointer"
      data-testid={PROFILE_BUTTON_TEST_ID}
      display={{ base: 'none', tablet: 'flex' }}
      gap="8px"
      minWidth="fit-content"
      flexShrink={0}
      {...rest}
    >
      {/* Profile Picture */}
      <Box width="26px" height="26px" borderRadius="50%" overflow="hidden" flexShrink={0} position="relative">
        <Condition
          expression={isAuthenticated}
          then={
            <Image
              src={`${getMediaOrigin()}${PROFILE_IMAGE_PATHS.USER_LOGO}`}
              alt="Profile"
              width="100%"
              height="100%"
              objectFit="cover"
            />
          }
          else={
            <Image
              src={`${getMediaOrigin()}${PROFILE_IMAGE_PATHS.GUEST_LOGO}`}
              alt="Profile"
              width="100%"
              height="100%"
              objectFit="cover"
            />
          }
        />
      </Box>

      {/* Text Content */}
      <Flex direction="column" gap="2px" alignItems="flex-start">
        <Condition
          expression={isAuthenticated}
          then={
            <>
              <Text textStyle="label.small" fontWeight="semibold" color="generic.0" whiteSpace="nowrap">
                Hi, {displayName}
              </Text>
              <Text textStyle="label.xsmall" fontWeight="regular" color="primary.200" whiteSpace="nowrap">
                {points} pts
              </Text>
            </>
          }
          else={
            <>
              <Text textStyle="label.small" fontWeight="semibold" color="generic.0" whiteSpace="nowrap">
                Sign In
              </Text>
              <Text textStyle="label.xsmall" fontWeight="regular" color="neutral.400" whiteSpace="nowrap">
                For free Shipping
              </Text>
            </>
          }
        />
      </Flex>
    </Flex>
  );
};

export default ProfileButton;
